<template>
  <form class="ui-panel ui-form" novalidate @submit.prevent="save">
    <ui-banner v-if="errorCount" ref="banner" role="alert" tabindex="-1">{{ rest || $t("delivery_firms.form.errors", { count: errorCount }) }}</ui-banner>

    <ui-section :title="$t('delivery_firms.sections.basic')">
      <div class="ui-grid">
        <ui-field v-model="form.name" name="firm-name" :label="$t('title')" :error="err('name')" required full />
        <ui-field v-if="parentOptions.length > 1 && !hasBranches" v-model="form.parentId" name="firm-parent" :label="$t('parent_company')" :error="err('parentId')" :options="parentOptions" />
        <ui-field v-model="form.active" name="firm-active" :label="$t('status')" :error="err('active')" :options="activeOptions" />
      </div>
    </ui-section>

    <ui-section :title="$t('delivery_firms.sections.contact')">
      <div class="ui-grid">
        <ui-field v-model="form.email" name="firm-email" :label="$t('email')" :error="err('email')" type="email" inputmode="email" required full />
        <ui-field v-model="form.phone" name="firm-phone" :label="$t('phone')" :error="err('phone')" type="tel" inputmode="tel" />
        <ui-field v-model="form.contact" name="firm-contact" :label="$t('contact_person')" :error="err('contact')" />
        <ui-field v-model="form.owner" name="firm-owner" :label="$t('owner')" :error="err('owner')" full />
        <ui-field v-model="form.countryId" name="firm-country" :label="$t('country')" :error="err('countryId')" :options="countryOptions" />
        <ui-field v-model="form.cityId" name="firm-city" :label="$t('city')" :error="err('cityId')" :options="cityOptions" />
      </div>
    </ui-section>

    <ui-section :title="$t('delivery_firms.sections.legal')">
      <div class="ui-grid">
        <ui-field v-model="form.jib" name="firm-jib" :label="$t('jib')" :error="err('jib')" />
        <ui-field v-model="form.pib" name="firm-pib" :label="$t('pib')" :error="err('pib')" />
        <ui-field v-model="form.contract" name="firm-contract" :label="$t('contract')" :error="err('contract')" full />
        <ui-field v-model="form.note" name="firm-note" :label="$t('note')" :error="err('note')" full />
      </div>
    </ui-section>

    <ui-section :title="$t('delivery_firms.sections.billing')">
      <div class="ui-grid">
        <ui-field v-model="form.currency" name="firm-currency" :label="$t('select_currency')" :error="err('currency')" :options="currencyOptions" />
        <div class="ui-field ui-field--full">
          <ui-check v-model="form.showBreakdown">{{ $t("show_price_breakdown") }}</ui-check>
        </div>
      </div>
    </ui-section>

    <ui-section v-if="shell.isAdmin" :title="$t('delivery_firms.sections.platform')">
      <div class="ui-grid">
        <ui-field v-model="form.platformType" name="firm-platform-type" :label="$t('platform_pay_type')" :error="err('platformType')" :options="platformOptions" />
        <ui-field v-model="form.platformAmount" name="firm-platform-amount" :label="`${$t('amount')} (${platformSuffix})`" :error="err('platformAmount')" inputmode="decimal" />
      </div>
    </ui-section>

    <div class="ui-form-actions">
      <ui-button :disabled="saving" @click="cancel">{{ $t("cancel") }}</ui-button>
      <ui-button type="submit" variant="primary" :disabled="saving">{{ isNew ? $t("add") : $t("save") }}</ui-button>
    </div>
  </form>
</template>

<script>
import { CURRENCY_OPTIONS } from "@/utils/currency";
import {
  PLATFORM_PAYING_TYPES,
  emptyFirmForm,
  formFromFirm,
  validateFirmForm,
} from "../firmsLogic";
import hasFirmLabels from "../hasFirmLabels";

/**
 * Unos i izmjena firme u panelu: osnovno, kontakt (sa državom i gradom), pravni podaci, obračun, a admin vidi i naplatu platforme.
 * Greška backenda (422) se prikazuje ispod polja na koje se odnosi, forma se ne zatvara i ništa se ne gubi.
 */
export default {
  name: "FirmFormPanel",
  mixins: [hasFirmLabels],
  inject: ["reportShell"],
  props: {
    firmId: { type: Number, default: 0 },
    /** nova podružnica: matična firma je već izabrana */
    parentId: { type: Number, default: 0 },
  },
  data() {
    const shell = this.reportShell;
    const firm = this.firmId ? shell.flat.find((item) => item.id === this.firmId) : null;
    const form = firm ? formFromFirm(firm, shell.countryOfCity) : emptyFirmForm();
    if (!firm && this.parentId) {
      form.parentId = String(this.parentId);
    }
    return { form, original: firm, errors: {}, rest: "", saving: false };
  },
  computed: {
    shell() {
      return this.reportShell;
    },
    isNew() {
      return !this.firmId;
    },
    hasBranches() {
      return Boolean(this.original && this.original.children.length);
    },
    errorCount() {
      return Object.keys(this.errors).length || (this.rest ? 1 : 0);
    },
    parentOptions() {
      const others = this.shell.firms
        .filter((item) => item.id !== this.firmId)
        .map((item) => ({
          value: String(item.id),
          label: `${this.firmName(item)} #${item.id}`,
        }));
      return [{ value: "", label: this.$t("delivery_firms.form.parent_none") }].concat(
        others
      );
    },
    activeOptions() {
      return [
        { value: "1", label: this.$t("delivery_firms.state.active") },
        { value: "0", label: this.$t("delivery_firms.state.inactive") },
        { value: "2", label: this.$t("delivery_firms.state.deleted") },
      ];
    },
    countryOptions() {
      const none = { value: "", label: this.$t("delivery_firms.form.not_selected") };
      return [none].concat(
        this.shell.countries.map((country) => ({
          value: String(country.id),
          label: this.toLatin(country.name),
        }))
      );
    },
    cityOptions() {
      const country = this.shell.countries.find(
        (item) => String(item.id) === String(this.form.countryId)
      );
      const cities = country ? country.city || [] : this.shell.allCities;
      const options = cities.map((city) => ({
        value: String(city.id),
        label: this.toLatin(city.name),
      }));
      // grad koji lista ne poznaje (nije u izabranoj državi) ostaje izabran dok ga korisnik ne promijeni
      if (
        this.form.cityId &&
        !options.some((option) => option.value === String(this.form.cityId))
      ) {
        options.push({
          value: String(this.form.cityId),
          label:
            this.toLatin(this.shell.cityName(Number(this.form.cityId))) ||
            `#${this.form.cityId}`,
        });
      }
      return [{ value: "", label: this.$t("delivery_firms.form.not_selected") }].concat(
        options
      );
    },
    currencyOptions() {
      return [{ value: "", label: this.$t("delivery_firms.form.not_selected") }].concat(
        CURRENCY_OPTIONS.map((item) => ({ value: item.val, label: item.text }))
      );
    },
    platformOptions() {
      return [{ value: "", label: this.$t("delivery_firms.form.not_selected") }].concat(
        PLATFORM_PAYING_TYPES.map((value) => ({
          value: String(value),
          label: this.payTypeLabel(value),
        }))
      );
    },
    platformSuffix() {
      return String(this.form.platformType) === "2"
        ? "%"
        : this.form.currency || this.restaurantCurrency || "KM";
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        // greška nestaje kad korisnik ispravi baš to polje
        Object.keys(this.errors).forEach((key) => {
          if (this.touched[key] !== this.form[key]) {
            this.$delete(this.errors, key);
          }
        });
      },
    },
    "form.countryId"(id) {
      // grad iz druge države se ne može zadržati
      const country = this.shell.countries.find((item) => String(item.id) === String(id));
      if (
        country &&
        this.form.cityId &&
        !(country.city || []).some((city) => String(city.id) === String(this.form.cityId))
      ) {
        this.form.cityId = "";
      }
    },
  },
  created() {
    this.touched = {};
  },
  methods: {
    err(key) {
      const code = this.errors[key];
      if (!code) return "";
      const known = {
        required: this.$t("required_field"),
        email: this.$t("delivery_firms.form.email_invalid"),
        number: this.$t("delivery_firms.form.number"),
      };
      return known[code] || code;
    },
    async save() {
      if (this.saving) return;
      this.rest = "";
      const local = validateFirmForm(this.form, { isAdmin: this.shell.isAdmin });
      if (Object.keys(local).length) {
        this.showErrors(local);
        return;
      }
      this.saving = true;
      try {
        const result = await this.shell.submitFirm(this.form, this.original);
        if (result && result.fields) {
          this.rest = result.rest || "";
          this.showErrors(result.fields);
        }
      } finally {
        this.saving = false;
      }
    },
    showErrors(errors) {
      this.touched = {};
      Object.keys(errors).forEach((key) => {
        this.touched[key] = this.form[key];
      });
      this.errors = { ...errors };
      this.$nextTick(() => {
        const first = this.$el.querySelector("[aria-invalid='true']");
        if (first) first.focus();
        else if (this.$refs.banner) this.$refs.banner.$el.focus();
      });
    },
    cancel() {
      this.shell.cancelForm(this.firmId);
    },
  },
};
</script>
